import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FileText, Archive, User, Settings, LogOut, ChevronLeft, ChevronRight, X, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import PartnerMark from '../PartnerMark';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const ClientSidebar = ({ isCollapsed, onToggle, onLogout, partnerInfo, subdomain, isMobileOpen, onMobileClose, useSubdomainRouting, isEmbedded = false, embedPartnerId = null, onStartTour }) => {
  const location = useLocation();
  const clientName = localStorage.getItem('clientName') || 'Client';

  // Get base path based on routing mode
  const getBasePath = () => {
    if (isEmbedded && embedPartnerId) {
      return `/embed/${embedPartnerId}`;
    }
    return '';  // subdomain routing — bare paths (/will, /dashboard, etc.)
  };
  
  const basePath = getBasePath();

  // Navigation items configuration
  const navItems = [
    { path: `${basePath}/dashboard`, icon: FileText, label: 'Will', tourId: 'tour-will' },
    { path: `${basePath}/life-vault`, icon: Archive, label: 'Life Vault', tourId: 'tour-life-vault' },
    { path: `${basePath}/profile`, icon: User, label: 'Profile', tourId: 'tour-profile' },
    { path: `${basePath}/settings`, icon: Settings, label: 'Settings' },
  ];

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className={`min-h-20 py-4 flex items-center border-b border-slate-800 ${isCollapsed && !isMobileOpen ? 'justify-center px-2' : 'px-4'}`}>
        <div className={`flex items-center min-w-0 ${isCollapsed && !isMobileOpen ? 'justify-center' : 'gap-3 w-full'}`}>
          <PartnerMark
            partnerInfo={partnerInfo}
            size="md"
            showName={!isCollapsed || isMobileOpen}
            nameClassName="text-sm tracking-tight leading-tight"
          />
        </div>
        {/* Mobile close button */}
        {isMobileOpen && (
          <button
            onClick={onMobileClose}
            className="lg:hidden ml-auto p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Client Info */}
      {(!isCollapsed || isMobileOpen) && (
        <div className="px-6 py-4 border-b border-slate-800">
          <p className="text-slate-400 text-xs uppercase tracking-wider">Welcome</p>
          <p className="text-white font-medium mt-1 truncate">{clientName}</p>
        </div>
      )}

      {/* Toggle Button - Desktop only, wrapped to prevent leak through translate-x-full on mobile */}
      <div className="hidden lg:block">
        <button
          onClick={onToggle}
          className="flex absolute -right-3 top-20 w-6 h-6 bg-slate-800 border border-slate-700 rounded-full items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors z-50"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 py-6 ${isCollapsed && !isMobileOpen ? 'px-3' : 'px-4'}`}>
        <div className={`flex flex-col ${isCollapsed && !isMobileOpen ? 'items-center space-y-3' : 'space-y-2'}`}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <NavItem 
                key={item.path} 
                item={item} 
                isCollapsed={isCollapsed && !isMobileOpen} 
                isActive={isActive}
                onMobileClose={onMobileClose}
              />
            );
          })}

          {/* Take a Tour — manual restart entry */}
          {onStartTour && (
            <TourNavItem
              isCollapsed={isCollapsed && !isMobileOpen}
              onClick={() => {
                if (onMobileClose) onMobileClose();
                onStartTour();
              }}
            />
          )}
        </div>
      </nav>

      {/* Powered by WillKart */}
      {(!isCollapsed || isMobileOpen) && (
        <div className="px-6 py-3 border-t border-slate-800">
          <p className="text-slate-500 text-xs text-center">
            Powered by <span className="text-amber-400">WillKart</span>
          </p>
        </div>
      )}

      {/* Logout */}
      <div className={`border-t border-slate-800 py-4 ${isCollapsed && !isMobileOpen ? 'px-3 flex justify-center' : 'px-4'}`}>
        {isCollapsed && !isMobileOpen ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onLogout}
                  className="w-12 h-12 flex items-center justify-center rounded-xl text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-slate-800 border-slate-700 text-white">
                Logout
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Button
            variant="ghost"
            onClick={onLogout}
            className="w-full justify-start gap-3 px-4 py-3 h-12 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </Button>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex fixed top-0 left-0 h-screen bg-slate-900 border-r border-slate-800 flex-col transition-all duration-300 z-40 ${
          isCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar */}
      <aside
        className={`lg:hidden fixed top-0 left-0 h-screen bg-slate-900 border-r border-slate-800 flex flex-col z-50 w-72 transition-transform duration-300 ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

// Reusable NavItem component
const NavItem = ({ item, isCollapsed, isActive, onMobileClose }) => {
  const Icon = item.icon;

  const handleClick = () => {
    if (onMobileClose) {
      onMobileClose();
    }
  };

  if (isCollapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <NavLink
              to={item.path}
              onClick={handleClick}
              data-tour-id={item.tourId || undefined}
              className={`w-12 h-12 flex items-center justify-center rounded-xl transition-all duration-200 ${
                isActive
                  ? 'bg-amber-500/20'
                  : 'hover:bg-slate-800'
              }`}
            >
              <Icon 
                className={`w-5 h-5 ${
                  isActive ? 'text-amber-400' : 'text-slate-400 hover:text-white'
                }`} 
              />
            </NavLink>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-slate-800 border-slate-700 text-white">
            {item.label}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <NavLink
      to={item.path}
      onClick={handleClick}
      data-tour-id={item.tourId || undefined}
      className={`flex items-center gap-3 px-4 py-3 h-12 rounded-xl transition-all duration-200 ${
        isActive
          ? 'bg-amber-500/20 text-amber-400'
          : 'text-slate-400 hover:bg-slate-800 hover:text-white'
      }`}
    >
      <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-amber-400' : ''}`} />
      <span className="font-medium">{item.label}</span>
    </NavLink>
  );
};

// Reusable button-style nav item for "Take a Tour" — restarts the onboarding tour
const TourNavItem = ({ isCollapsed, onClick }) => {
  if (isCollapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onClick}
              data-testid="sidebar-start-tour-btn"
              className="w-12 h-12 flex items-center justify-center rounded-xl transition-all duration-200 hover:bg-slate-800 text-slate-400 hover:text-amber-300"
            >
              <Sparkles className="w-5 h-5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-slate-800 border-slate-700 text-white">
            Take a Tour
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return (
    <button
      onClick={onClick}
      data-testid="sidebar-start-tour-btn"
      className="flex items-center gap-3 px-4 py-3 h-12 rounded-xl transition-all duration-200 text-slate-400 hover:bg-slate-800 hover:text-amber-300 text-left"
    >
      <Sparkles className="w-5 h-5 flex-shrink-0" />
      <span className="font-medium">Take a Tour</span>
    </button>
  );
};

export default ClientSidebar;
